<script setup>
import {computed, ref, watch} from "vue";
import { DateTime } from 'luxon';
import ServiceButton from "../components/ui-components/ServiceButton.vue";
import Dialog from "primevue/dialog";
import ChangeReservationDialog from "../components/ui-components/ChangeReservationDialog.vue";
import CancelReservationDialog from "../components/ui-components/CancelReservationDialog.vue";
import ConfirmDialog from "primevue/confirmdialog";
import ContactDialog from "../components/ui-components/ContactDialog.vue";
import {getServiceName, percentToHex} from "../../../helpers.js";
import {router, usePage} from "@inertiajs/vue3";
import {useConfirm} from "primevue/useconfirm";
import PoweredByRoori from "../../administration/components/ui-components/PoweredByRoori.vue";
import {Head} from "@inertiajs/vue3";
import {trans} from "laravel-vue-i18n";
import EmbedMap from "../components/ui-components/EmbedMap.vue";

const props = defineProps({
    forms: Object,
    reservation: Object,
    location: Object,
    registrationForm: Object,
    locale: String,
})
const confirm = useConfirm()
const theme = computed({
    get() {
        if (darkMode.value) {
            return {
                color: props.forms.theme.color,
                borderless: props.forms.theme.borderless,
                transparent: props.forms.theme.transparent,
                darkMode: true,
                darkModeLock: props.forms.theme.darkModeLock,
                background_color: props.forms.theme.background_color_dark ?? props.forms.theme.background_color,
                background_image_url: props.forms.theme.background_image_url_dark ?? props.forms.theme.background_image_url,
                logo_url: props.forms.theme.logo_url_dark ?? props.forms.theme.logo_url,
                panel_color: props.forms.theme.panel_color_dark ?? props.forms.theme.panel_color,
                panel_transparency: props.forms.theme.panel_transparency_dark ?? props.forms.theme.panel_transparency,
                panel_backdrop_blur: props.forms.theme.panel_backdrop_blur_dark ?? props.forms.theme.panel_backdrop_blur,
                fontFamily: props.forms.theme.fontFamily,
                rows: props.forms.theme.rows,
                columns: props.forms.theme.columns,
                roundness: props.forms.theme.roundness,
            }
        }
        return {
            color: props.forms.theme.color,
            borderless: props.forms.theme.borderless,
            transparent: props.forms.theme.transparent,
            darkMode: false,
            darkModeLock: props.forms.theme.darkModeLock,
            background_color: props.forms.theme.background_color,
            background_image_url: props.forms.theme.background_image_url,
            logo_url: props.forms.theme.logo_url,
            panel_color: props.forms.theme.panel_color,
            panel_transparency: props.forms.theme.panel_transparency,
            panel_backdrop_blur: props.forms.theme.panel_backdrop_blur,
            fontFamily: props.forms.theme.fontFamily,
            rows: props.forms.theme.rows,
            columns: props.forms.theme.columns,
            roundness: props.forms.theme.roundness,
        }
    },
    set(val) {
        Object.assign(props.forms.theme, val);
    }
})
const previewStyle = computed(() => ({
    backgroundImage: theme.value.background_image_url ? `url(${theme.value.background_image_url})` : '',
    backgroundColor: `#${theme.value.background_color}`,
}));

const darkMode = ref(false);

const greeting = computed(() => {
    const hour = Math.floor(new Date().getHours());
    const salutation = props.reservation.salutation;
    if (hour > 4 && hour < 9) {
        // Dobré ráno, {salutation}
        return trans('widget.greeting_morning', { salutation })
    }
    if (hour >= 9 && hour < 12) {
        // Dobré dopoledne, {salutation}
        return trans('widget.greeting_forenoon', { salutation });
    }
    if (hour >= 12 && hour < 17) {
        // Dobré odpoledne, {salutation}
        return trans('widget.greeting_afternoon', { salutation });
    }
    if (hour >= 17 && hour < 22) {
        // Dobrý večer, {salutation}
        return trans('widget.greeting_evening', { salutation });
    }
    if ((hour >= 0 && hour < 4) || (hour >= 22)) {
        // Dobrou noc, {salutation}
        return trans('widget.greeting_night', { salutation });
    }
});

const changeDialogModel = ref(false);
const contactDialogModel = ref(false);

const billItems = computed(() => props.reservation.bill_items ?? [])
const billTotal = computed(() => billItems.value.reduce((s, i) => s + parseFloat(i.unit_price_with_vat) * parseInt(i.amount), 0))
const billWithoutVat = computed(() => billItems.value.reduce((s, i) => s + parseFloat(i.unit_price_without_vat) * parseInt(i.amount), 0))
const billVat = computed(() => billTotal.value - billWithoutVat.value)

const page = usePage()
function formatPrice(amount) {
    const loc = props.locale ?? page.props.locale ?? 'cs-CZ'
    return new Intl.NumberFormat(loc, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)
}

const cancel = () => {
    if (props.reservation.can_cancel) {
        confirm.require({
            message: trans('widget.confirm_cancel_message'), // Opravdu chcete zrušit svou rezervaci?
            header: trans('widget.confirm_cancel_header'), // Jste si jisti?
            icon: 'pi pi-info-circle',
            rejectLabel: trans('widget.reject_cancel_label'), // Zrušit
            rejectProps: {
                label: trans('widget.reject_cancel_label'), // Zrušit
                severity: 'secondary',
                outlined: true
            },
            acceptProps: {
                label: trans('widget.accept_cancel_label'), // Zrušit rezervaci
                severity: 'danger'
            },
            accept: () => {
                router.post(props.reservation.client_url + '/cancel')
            },
        })
    }
}


watch(() => props.forms.theme.mode_lock, (val) => {

    if (val === 'auto') {
        if (!window.matchMedia) {
            return;
        }
        const query = window.matchMedia('(prefers-color-scheme: dark)');
        if (query.matches) {
            darkMode.value = true;
            document.querySelector('html')?.classList.add('dark');
        } else {
            darkMode.value = false;
            document.querySelector('html')?.classList.remove('dark');
        }
    } else {
        val === 'light' ? document.querySelector('html')?.classList.remove('dark') : document.querySelector('html')?.classList.add('dark');

        darkMode.value = val === 'dark';
    }
}, {immediate: true})
</script>

<template>
    <div class="w-full h-full bg-no-repeat bg-cover p-4 pb-0 height-animate" :style="previewStyle"
         style="min-height: 100dvh">
        <Head :title="$t('widget.booking_name', {name: reservation.summary})"></Head>
        <ConfirmDialog></ConfirmDialog>
        <!-- HEADER -->
        <div class="flex items-center justify-center py-9 pb-12">
            <img v-if="theme.logo_url" :src="theme.logo_url" width="200" height="200"/>
        </div>
        <!-- BODY -->
        <div class="container color-transition overflow-hidden transition-all"
             :class="[{
            'rounded-xl': theme.roundness === 1,
            'rounded-2xl': theme.roundness === 2,
            'rounded-3xl': theme.roundness === 3,
            'backdrop-filter backdrop-blur-lg': theme.panel_backdrop_blur,
            'border': theme.panel_transparency > 50,
            'border-neutral-700': theme.darkMode
        }]"
             :style="{
            backgroundColor: `#${theme.panel_color}${percentToHex(theme.panel_transparency)}`
        }">
            <EmbedMap v-if="location?.type === 'in_person'" :coordinates="{ lat: location.latitude, lng: location.longitude }" class="border-b dark:border-neutral-700" :color="theme.color" :height="120" />
            <div class="max-md:p-8 md:p-12  transition-all">
                <div class="flex max-md:flex-col-reverse max-md:gap-4 max-md:items-start pb-6 mb-12">
                    <div class="grow">
                        <!-- {greeting}! -->
                        <h2 class="text-4xl mb-2">{{ greeting }}</h2>
                        <template v-if="reservation.state === 'rejected'">
                            <!-- Vaše rezervace byla zrušena -->
                            <p>{{ $t('widget.reservation_cancelled') }}</p>
                        </template>
                        <template v-else>
                            <p :class="reservation.reservation_change_requests.length ? 'line-through opacity-50': ''">
                                <template v-if="reservation.time_start">
                                    {{ $t('widget.reservation_scheduled', {
                                        formatted_date: reservation.formatted_date,
                                        time_start: reservation.time_start.substring(0, 5),
                                        time_end: reservation.time_end.substring(0, 5)
                                    }) }}
                                </template>
                                <template v-else>
                                    {{ $t('widget.reservation_scheduled_allday', {
                                        date_start: reservation.formatted_date,
                                        date_end: reservation.formatted_date_end ?? reservation.formatted_date
                                    }) }}
                                </template>
                            </p>
                            <div
                                v-if="reservation.reservation_change_requests.length && reservation.reservation_change_requests[0].action === 'change'"
                                class="flex gap-2">
                                <!-- Vaše rezervace bude přeplánována na {formatted_date} od {new_time_start} do {new_time_end}. -->
                                <p>{{ $t('widget.reservation_rescheduled', {
                                    formatted_date: reservation.reservation_change_requests[0].formatted_date,
                                    new_time_start: reservation.reservation_change_requests[0].new_time_start.substring(0, 5),
                                    new_time_end: reservation.reservation_change_requests[0].new_time_end.substring(0, 5)
                                }) }}</p>
                                <!-- Čeká na schválení -->
                                <span class="text-sm px-2 pt-0.5 border border-black rounded-xl">{{ $t('widget.waiting_for_approval') }}</span>
                            </div>
                            <p v-if="reservation.reservation_change_requests.length && reservation.reservation_change_requests[0].action === 'cancel'"
                               class="text-red-700"><span class="pi pi-exclamation-circle"></span>
                                <!-- Vaše rezervace čeká na potvrzení zrušení -->
                                {{ $t('widget.waiting_for_cancellation_confirmation') }}
                            </p>
                            <p v-if="reservation.services">
                                <!-- Jste přihlášeni na službu {services.name} -->
                                {{ $t('widget.registered_for_service', { service_name: reservation.services.name }) }}
                                <span v-if="reservation.services.price">
                                <!-- za {price} / {unit} -->
                                {{ $t('widget.service_price_unit', {
                                    price: new Intl.NumberFormat($page.props.locale, { style: 'currency', currency: reservation.services.currency, maximumFractionDigits: 0 }).format(reservation.services.price),
                                    unit: reservation.services.unit
                                }) }}
                            </span>.
                                <span v-if="reservation.workers">
                                <!-- Obslouží Vás {workers.name}. -->
                                {{ $t('widget.served_by_worker', { worker_name: reservation.workers.name }) }}
                            </span>
                            </p>
                            <p v-if="location?.name">
                                {{ $t(location.type === 'in_person' ? 'widget.in_person' : 'widget.remote', { location: getServiceName(location), name: location.name }) }}
                            </p>
                        </template>
                    </div>

                    <div v-if="reservation.state !== 'rejected'" class="flex max-md:hidden flex-col gap-3" :class="reservation.state !== 'pending' ? 'items-center' : 'items-end'">
                        <div class="border rounded-xl overflow-hidden shadow-xl dark:border-neutral-700" style="width: 120px">
                            <p class="p-2 text-center text-white" :class="`bg-${theme.color}-500`">
                                {{ DateTime.fromISO(reservation.date).setLocale($page.props.locale).toFormat('LLLL') }}
                            </p>
                            <div class="text-center p-4 shadow-inner-xl bg-white dark:bg-neutral-900">
                                <p class="text-3xl">{{ DateTime.fromISO(reservation.date).setLocale($page.props.locale).toFormat('dd') }}</p>
                                <p>{{ DateTime.fromISO(reservation.date).setLocale($page.props.locale).toFormat('cccc') }}</p>
                            </div>
                        </div>
                        <div v-if="reservation.state === 'pending'" :class="`bg-gray-400 px-3 rounded-lg text-white shadow mt-2 -mb-9`">
                            <!-- Čeká na potvrzení -->
                            {{ $t('widget.waiting_for_confirmation') }}
                        </div>
                        <div v-if="reservation.state === 'accepted'" :class="` bg-${theme.color}-500 px-3 rounded-lg text-white shadow mt-2 -mb-9`">
                            <span class="pi pi-check mt-1 mr-1"></span>
                            <!-- Potvrzeno -->
                            {{ $t('widget.confirmed') }}
                        </div>
                    </div>
                    <div v-if="reservation.state === 'rejected'" :class="` bg-red-500 px-3 rounded-lg text-white shadow flex items-center h-6`">
                        <span class="pi pi-times mr-1"></span>
                        <!-- Zrušeno -->
                        {{ $t('widget.cancelled') }}
                    </div>
                </div>
                <!-- Rental item info + Bill -->
                <template v-if="reservation.rental_items || billItems.length">
                  <div class="mb-8 p-4 rounded-lg border border-gray-200 dark:border-neutral-700 bg-white/50 dark:bg-neutral-900/50 flex flex-col gap-4">
                    <!-- Rental item -->
                    <div v-if="reservation.rental_items" class="flex gap-3 items-center">
                      <img
                        v-if="reservation.rental_items.gallery?.[0]?.full_url"
                        :src="reservation.rental_items.gallery[0].full_url"
                        class="w-14 h-14 rounded-lg object-cover shrink-0"
                        :alt="reservation.rental_items.name"
                      />
                      <div v-else class="w-14 h-14 rounded-lg shrink-0 flex items-center justify-center" :class="`bg-${theme.color}-100 dark:bg-${theme.color}-900`">
                        <i class="pi pi-box text-xl" :class="`text-${theme.color}-500`"></i>
                      </div>
                      <div>
                        <p class="text-xs text-gray-500 dark:text-gray-400">{{ $t('widget.selected_rental') }}</p>
                        <p class="font-semibold text-gray-800 dark:text-white">{{ reservation.rental_items.name }}</p>
                        <p v-if="reservation.rental_items.description" class="text-sm text-gray-500 dark:text-gray-400 line-clamp-1" v-html="reservation.rental_items.description"></p>
                      </div>
                    </div>

                    <!-- Bill -->
                    <div v-if="billItems.length" :class="{ 'border-t dark:border-neutral-700 pt-4': reservation.rental_items }">
                      <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">{{ $t('widget.bill') }}</p>
                      <table class="w-full text-sm">
                        <thead>
                          <tr class="text-gray-400 dark:text-gray-500 border-b dark:border-neutral-700 text-xs">
                            <th class="text-left pb-1 pr-2 font-normal">{{ $t('widget.bill_unit_price') }}</th>
                            <th class="text-right pb-1 px-2 font-normal">{{ $t('widget.bill_quantity') }}</th>
                            <th class="text-right pb-1 pl-2 font-normal">{{ $t('widget.bill_total') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="item in billItems" :key="item.id" class="border-b border-gray-100 dark:border-neutral-800 last:border-0">
                            <td class="py-1 pr-2 text-gray-800 dark:text-gray-200">{{ item.description }}</td>
                            <td class="text-right py-1 px-2 text-gray-500 dark:text-gray-400 tabular-nums">{{ item.amount }}×</td>
                            <td class="text-right py-1 pl-2 text-gray-800 dark:text-gray-200 tabular-nums">{{ formatPrice(parseFloat(item.unit_price_with_vat) * parseInt(item.amount)) }}</td>
                          </tr>
                        </tbody>
                      </table>
                      <div class="mt-2 pt-2 border-t dark:border-neutral-700 text-sm space-y-0.5">
                        <div v-if="billVat > 0.01" class="flex justify-between text-gray-500 dark:text-gray-400">
                          <span>{{ $t('widget.bill_without_vat') }}</span>
                          <span class="tabular-nums">{{ formatPrice(billWithoutVat) }}</span>
                        </div>
                        <div v-if="billVat > 0.01" class="flex justify-between text-gray-500 dark:text-gray-400">
                          <span>{{ $t('widget.bill_vat') }}</span>
                          <span class="tabular-nums">{{ formatPrice(billVat) }}</span>
                        </div>
                        <div class="flex justify-between font-semibold text-gray-800 dark:text-white">
                          <span>{{ $t('widget.bill_total') }}</span>
                          <span class="tabular-nums">{{ formatPrice(billTotal) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <div class="flex gap-4 max-md:justify-between">

                    <template v-if="reservation.state !== 'rejected'">
                        <service-button @click="() => {
                    if (reservation.can_change)
                        changeDialogModel = true
                }" :disabled="!reservation.can_change" :disabled-overlay="$t('widget.change_reservation_not_possible')"
                                        :color="theme.color" :title="$t('widget.change_reservation')" icon="pi pi-calendar-clock"
                                        :description="$t('widget.change_reservation_description')"></service-button>
                        <service-button @click="cancel" :disabled="!reservation.can_cancel"
                                        :disabled-overlay="$t('widget.cancellation_not_possible')"
                                        :color="theme.color" :title="$t('widget.cancel_reservation')" icon="pi pi-times"
                                        :description="$t('widget.cancel_reservation_description')"></service-button>
                    </template>
                    <div class="grow max-md:hidden"></div> <!-- Kontakt -->
                    <service-button @click="contactDialogModel = true" :color="theme.color" :title="$t('widget.contact')"
                                    icon="pi pi-phone"></service-button>
                </div>

                <change-reservation-dialog v-model:visible="changeDialogModel" :theme="theme"></change-reservation-dialog>
                <contact-dialog v-model:visible="contactDialogModel" :url="`/send-contact-form/${reservation.id}`"></contact-dialog>

            </div>

        </div>
    </div>

    <div class="container mx-auto flex flex-col items-center justify-center gap-4 py-6 min-h-[100px]">
        <powered-by-roori v-if="forms.show_banner"></powered-by-roori>
    </div>
</template>

<style scoped lang="scss">

</style>
